type ToneType = 'sine' | 'square' | 'sawtooth' | 'triangle';

interface ToneOptions {
    frequency: number;
    type: ToneType;
    duration: number;
    volume?: number;
    startOffset?: number;
}

class TonePlayer {
    private ctx: AudioContext | null = null;

    private async getContext(): Promise<AudioContext | null> {
        if (typeof window === 'undefined') {
            return null;
        }

        let ctx = this.ctx;
        if (!ctx) {
            const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
            if (!AudioContextClass) {
                return null;
            }
            ctx = new AudioContextClass() as AudioContext;
            this.ctx = ctx;
        }

        if (ctx.state === 'suspended') {
            try {
                await ctx.resume();
            } catch {
                // Browser may block audio until a user gesture; ignore silently.
            }
        }

        return ctx;
    }

    private async playTone(options: ToneOptions): Promise<void> {
        const ctx = await this.getContext();
        if (!ctx) {
            return;
        }

        const startTime = ctx.currentTime + (options.startOffset || 0);
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.type = options.type;
        oscillator.frequency.setValueAtTime(options.frequency, startTime);

        const volume = Math.min(1, Math.max(0, options.volume ?? 0.8));
        gainNode.gain.setValueAtTime(volume, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + options.duration);

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.start(startTime);
        oscillator.stop(startTime + options.duration);
    }

    async playSuccess(): Promise<void> {
        const ctx = await this.getContext();
        if (!ctx) return;

        const base = ctx.currentTime;

        const schedule = (startSec: number, freq: number, dur: number) => {
            const osc  = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, startSec);
            gain.gain.setValueAtTime(0.9, startSec);
            gain.gain.exponentialRampToValueAtTime(0.001, startSec + dur);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(startSec);
            osc.stop(startSec + dur);
        };

        schedule(base,        1200, 0.12);
        schedule(base + 0.13, 1500, 0.25);
    }

    async playError(): Promise<void> {
        const ctx = await this.getContext();
        if (!ctx) return;

        const noteDur = 0.18;
        const gap = 0.03;
        const base = ctx.currentTime;

        const schedule = (startSec: number) => {
            const osc  = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'square';
            osc.frequency.setValueAtTime(80, startSec);
            gain.gain.setValueAtTime(0.0001, startSec);
            gain.gain.linearRampToValueAtTime(1.0, startSec + 0.01);
            gain.gain.setValueAtTime(1.0, startSec + noteDur - 0.02);
            gain.gain.linearRampToValueAtTime(0.0001, startSec + noteDur);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(startSec);
            osc.stop(startSec + noteDur);
        };

        schedule(base);
        schedule(base + noteDur + gap);
    }
}

const tonePlayer = new TonePlayer();

export const playSuccessTone = (): Promise<void> => tonePlayer.playSuccess();
export const playErrorTone = (): Promise<void> => tonePlayer.playError();
